import {
  isArrayOf,
  isEither,
  isNull,
  isNumber,
  isOptional,
  isShapeOf,
  isString,
} from 'giltig';

import type { PermissionGroupName } from '@studio/core/utils/permission';
import type { BoltEnv } from '@studio/utils/environment';
import type { Branded } from '@studio/utils/types';

import type { SNOWTicketStage, SNOWTicketState } from './utils';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare const STUDIO_IDENTITY_USER_ID_SYMBOL: unique symbol;
export type StudioIdentityUserId = Branded<
  `si:${BoltEnv}:${string}`,
  typeof STUDIO_IDENTITY_USER_ID_SYMBOL
>;

export type UserId = StudioIdentityUserId | string;

export interface StudioIdentityTokenPayload {
  readonly iss?: string;
  readonly sub: StudioIdentityUserId | string;
  readonly iat?: number;
  readonly exp: number;
  readonly email: string;
  readonly mode: 'hybrid' | 'modern';
  readonly provider: string;
  readonly realm: string;
  /** @deprecated - This is a combination of both legacy and permission group roles! */
  readonly roles: readonly string[];
  readonly legacyRoles?: readonly string[];
  readonly legacyRoleConditions?: Record<string, string>;
  readonly groups: readonly PermissionGroupName[];
  readonly masqueraded: boolean | null;
}

export interface StudioIdentityTokenPayloadWithToken extends StudioIdentityTokenPayload {
  readonly token: StudioIdentityToken;
}

export type StudioIdentityToken = Branded<string, 'StudioIdentityToken'>;

export interface StudioIdentityTokenResponse {
  readonly token: StudioIdentityToken;
}

export interface TeamMembersResponse {
  readonly users: readonly StudioIdentityBasicUserInfo[];
  readonly meta: {
    readonly page: number;
    readonly pageSize: number;
    readonly totalItems: number;
  };
}

export interface StudioIdentityBasicUserInfo {
  readonly id: UserId;
  readonly email: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly managerTwId?: string;
  readonly userTwId?: string;
}

// All decorators are optional
export interface UserDecoration {
  readonly slackUserId?: string;
  readonly jiraUserId?: string;
  readonly locale?: string;
  readonly displayName?: string;
  readonly image48?: string;
  readonly image512?: string;
  readonly phone?: string;
  readonly title?: string;
  readonly namePronunciation?: string;
  readonly timezone?: string;
  readonly tzLabel?: string;
  readonly tzOffset?: number;
  readonly startDate?: string;
  readonly statusEmoji?: string;
  readonly statusText?: string;
  readonly oooMessage?: string;
}

export interface StudioIdentityUserInfo extends StudioIdentityBasicUserInfo {
  /** @deprecated - These are the legacy roles */
  readonly roles: readonly string[];
  readonly groups: readonly PermissionGroupName[];
  readonly publishingSites: readonly string[];
  readonly legacyId?: string;
  readonly legacyEmail?: string;
  readonly title?: string;
  readonly teams?: readonly string[];
}

export interface StudioIdentityUserInfoResponse {
  readonly id: UserId;
  readonly email: string;
  /** @deprecated - These are the legacy roles */
  readonly roles: readonly string[];

  readonly groups: readonly PermissionGroupName[];
  readonly firstName?: string;
  readonly lastName?: string;

  readonly userTwid?: string;
}

export interface StudioIdentityUsersRequest {
  readonly page?: number;
  readonly pageSize?: number;
  readonly email?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly login?: string;
  readonly sortColumn?: string;
  readonly sortOrder?: 'asc' | 'desc';
}

export interface StudioIdentityUsersReportRequest {
  readonly email?: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly sortColumn?: string;
  readonly sortOrder?: 'asc' | 'desc';
}

export interface StudioIdentityUsersByGroupParams {
  readonly page?: number;
  readonly pageSize?: number;
}

export interface StudioIdentityUsersByGroupRequest {
  readonly permissionGroup: string;
  readonly query: StudioIdentityUsersByGroupParams;
}

export interface StudioIdentityUsersByGroupsRequest {
  readonly permissionGroups: string[];
}

export interface StudioIdentityAccessRequestsParams {
  readonly page: number;
  readonly pageSize: number;
}
export interface StudioIdentityAccessRequestsMeta {
  readonly page: number;
  readonly pageSize: number;
  readonly totalItems: number;
}

export interface StudioIdentityUsersMeta {
  readonly page: number;
  readonly pageSize: number;
  readonly totalItems: number;
}

export interface StudioIdentityUsersResponse {
  readonly users: readonly StudioIdentityUserInfo[];
  readonly meta: StudioIdentityUsersMeta;
}

export interface StudioIdentityUsersReportResponse {
  readonly data: readonly StudioIdentityUserInfo[];
  readonly meta: StudioIdentityUsersMeta;
}

export interface StudioIdentityUsersByRoleOrGroupResponse {
  readonly users: readonly StudioIdentityUserInfo[];
  readonly meta: StudioIdentityUsersMeta;
}

export interface GetTeamsResponse {
  readonly teams: readonly IdentityTeam[];
  readonly meta: {
    readonly page: number;
    readonly pageSize: number;
    readonly totalItems: number;
  };
}

export interface SnowApprover {
  approverGroupName?: string;
  approverGroupManagerUserTwId?: string;
  approverUserTwId?: string;
}

export interface SNOWItem {
  catalogueItem: string;
  ritmNumber: string;
  openedBy: string;
  requestedFor: string;
  requestedForEmail: string;
  shortDescription: string;
  stage: SNOWTicketStage;
  state: SNOWTicketState;
  systemId: string;
  workNotes: string;
  detailDescription: string;
  updatedOn: string;
  createdOn: string;
  closedAt: string;
  permissionGroupData: PermissionGroupData | null;
  approvers: SnowApprover[];
  approverGroups?: string;
}

export interface SnowRequestsByTwId {
  ritmNumber: string;
  stage: string;
  updatedOn: string;
  createdOn: string;
  permissionGroupData: PermissionGroupData | null;
  approvers: SnowApprover[];
}

export interface SnowRequestsByTwIdResponse {
  result: SnowRequestsByTwId[];
  meta: {
    page: number;
    pageSize: number;
    totalItems: number;
  };
}

export type GetSNOWRequestsResponse = {
  result: SNOWItem[];
  meta: {
    page: number;
    pageSize: number;
    totalItems: number;
  };
};

export type CreateSNOWRequestsResponse = {
  result: { status: string; ritmNumber: string };
};

interface Condition {
  [key: string]: string;
}

export interface IdentityTeam {
  readonly displayName: string;
  readonly description: string;
  readonly id: string;
  readonly metadata: object;
  readonly owner: string;
  readonly category: string;
  readonly approverGroup: string;
  readonly conditions: Condition[];
}

export enum SNOWRequestType {
  NEW_ACCESS = 'new_access',
  DELETE_ACCESS = 'delete_access',
}
export interface SNOWRequestPayload {
  requestType: SNOWRequestType;
  permissionGroupName: PermissionGroupName;
  businessPurpose: string;
}

interface PermissionGroupData {
  category?: string;
  displayName?: string;
  studioExperience?: string;
  permissionGroupName?: string;
}

export const isIdentityTeamResponse = isShapeOf({
  team: isShapeOf({
    id: isString,
    name: isString,
    owner: isString,
    description: isOptional(isString),
  }),
});

const isMeta = isShapeOf({
  page: isNumber,
  pageSize: isNumber,
  totalItems: isNumber,
});

export const isUserInGroup = isShapeOf({
  users: isArrayOf(
    isShapeOf({
      id: isString,
      email: isString,
      firstName: isOptional(isString),
      lastName: isOptional(isString),
      managerTwId: isOptional(isString),
      title: isOptional(isString),
      legacyEmail: isOptional(isString),
    }),
  ),
  meta: isMeta,
});

const isApprover = isShapeOf({
  approverGroupManagerUserTwId: isOptional(isString),
  approverGroupName: isOptional(isString),
  approverUserTwId: isOptional(isString),
});

const isPermissionGroupDetails = isShapeOf({
  category: isOptional(isString),
  displayName: isOptional(isString),
  permissionGroupName: isOptional(isString),
  studioExperience: isOptional(isString),
});

const isSnowItem = isShapeOf({
  approvers: isArrayOf(isApprover),
  catalogueItem: isString,
  closedAt: isString,
  createdOn: isString,
  detailDescription: isString,
  permissionGroupData: isEither(isPermissionGroupDetails, isNull),
  requestedFor: isString,
  requestedForEmail: isString,
  ritmNumber: isString,
  shortDescription: isString,
  stage: isString,
  state: isString,
  systemId: isString,
  updatedOn: isString,
  workNotes: isString,
  openedBy: isString,
});

const isSnowRequestByTwId = isShapeOf({
  ritmNumber: isString,
  stage: isString,
  updatedOn: isString,
  createdOn: isString,
  permissionGroupData: isEither(isPermissionGroupDetails, isNull),
  approvers: isArrayOf(isApprover),
});

export const isGetSnowAccessRequestsByTwIdResponse = isShapeOf({
  result: isArrayOf(isSnowRequestByTwId),
  meta: isMeta,
});

export const isGetSnowRequestsResponse = isShapeOf({
  result: isArrayOf(isSnowItem),
  meta: isShapeOf({
    page: isNumber,
    pageSize: isNumber,
    totalItems: isNumber,
  }),
});
